/**
 * Wire types — the exact shapes the payment user plane answers with
 * (`/api/v1`, Plan 02 Phase 0 + the 06 plane). Locked on the server by
 * `server/lib/entitlement/entitlements.contract.json`; the kit renders these
 * shapes and never re-derives server facts client-side.
 */

/** `GET /api/v1/plans` — the one catalog: per-service free thresholds + packs. */
export interface PlanPrice {
  usd: string | null;
  try: string | null;
  eur: string | null;
}

/** The optional per-country resolved price (present only when `?country=` was asked). */
export interface PlanLocalPrice {
  currency: string;
  subtotal: string;
  discount: string;
  tax: string;
  gross: string;
  taxMode: string;
  provider: string;
  providerPriceId: string | null;
}

export interface Plan {
  id: string;
  serviceId: string;
  tier: string;
  rank: number;
  name: string;
  description: string | null;
  billingCycle: "monthly" | "yearly" | "one_time";
  trialDays: number;
  taxMode: string;
  prices: PlanPrice;
  /** The quota vocabulary per plan — `monthly_storage_bytes`, `monthly_ai_tokens`, … */
  limits: Record<string, number>;
  localPrice: PlanLocalPrice | null;
}

/** `GET /api/v1/entitlements/me` — the display snapshot (contract: userPlane.snapshotFields). */
export type AccessLevel = "full" | "limited" | "read_only" | "restricted";

export interface EntitlementSnapshotView {
  serviceId: string;
  subscriptionId: string;
  planId: string;
  tier: string;
  rank: number;
  status: string;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  accessLevel: AccessLevel;
  quotaMultiplier: number;
  accessReason: string | null;
  limits: Record<string, number>;
  quotaType: string | null;
  quotaWindow: "period" | "daily" | null;
  rounding: "ceil" | "round" | "floor" | null;
  pauseResumeAt: string | null;
}

/** A service that could not produce a snapshot — the list stays renderable. */
export interface UnavailableEntitlement {
  serviceId: string;
  unavailable: true;
}

export type EntitlementEntry = EntitlementSnapshotView | UnavailableEntitlement;

export function isUnavailable(entry: EntitlementEntry): entry is UnavailableEntitlement {
  return (entry as UnavailableEntitlement).unavailable === true;
}

/** `GET /api/v1/usage/me` — the aggregated meters the UI renders. */
export type UsageMeterUnit = "byte" | "token" | "count";
export type UsageMeterSource = "byte_ledger" | "quota_counter" | "policy" | "none";

/** The meter row. Named `UsageMeterData` client-side — the component owns the
 *  `UsageMeter` name. */
export interface UsageMeterData {
  key: string;
  used: number;
  /** Exact decimal string for byte meters — JSON numbers lose precision past 2^53. */
  usedExact: string | null;
  limit: number;
  limitExact: string | null;
  unit: UsageMeterUnit;
  window: "period" | "daily" | null;
  resetAt: string | null;
  source: UsageMeterSource;
  /** True when the counter read failed — the UI must not render 0 as fact. */
  degraded: boolean;
}

export interface ServiceUsage {
  serviceId: string;
  tier: string | null;
  status: string | null;
  accessLevel: AccessLevel | null;
  accessReason: string | null;
  unavailable: boolean;
  meters: UsageMeterData[];
}

export interface UsageResponse {
  generatedAt: string;
  services: ServiceUsage[];
}

/** `GET /api/v1/subscriptions/me` — one row per subscription, quota on live rows. */
export interface SubscriptionSummary {
  id: string;
  serviceId: string;
  planId: string;
  planName: string;
  tier: string;
  rank: number;
  status: string;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  pendingPlanId: string | null;
  scheduledChangeAt: string | null;
  recurringMode: string;
  provider: string | null;
  price: {
    amount: string | null;
    currency: string | null;
    billingCycle: string | null;
  };
  quota: SerializedDecision | null;
}

/** The shared QuotaDecision wire shape (contract: fields). */
export interface SerializedDecision {
  serviceId: string;
  allowed: boolean;
  reason: string | null;
  subscriptionId: string | null;
  planId: string | null;
  tier: string | null;
  accessLevel: string | null;
  quotaType: string | null;
  used: number | null;
  limit: number | null;
  remaining: number | null;
  resetAt: string | null;
  suggestedPlanId: string | null;
  degraded: boolean;
}

/** `POST /api/v1/checkout` — the provider door. */
export interface CheckoutAnswer {
  paymentId: string;
  checkoutUrl: string;
  mode: "iframe" | "redirect";
  expiresAt: string;
  state: string;
  provider: string;
  fallbackFrom?: string;
  riskAction: string;
}

/** `GET /api/v1/payments/:id` (+ SSE events) — the payment's live status. */
export interface PaymentView {
  id: string;
  subscriptionId: string | null;
  status: string;
  provider: string | null;
  currency: string | null;
  subtotal: string;
  discountAmount: string;
  taxAmount: string;
  amountGross: string;
  errorCode: string | null;
  errorMessage: string | null;
  expiresAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

/** `GET /api/v1/invoices` — one document row (render subset; totals are string decimals). */
export interface InvoiceView {
  id: string;
  number: string | null;
  status: string;
  currency: string;
  subtotal: string;
  discountAmount: string;
  taxAmount: string;
  total: string;
  issuedBy: string;
  issuedAt: string | null;
  paidAt: string | null;
  createdAt: string;
}

export interface InvoicePage {
  invoices: InvoiceView[];
  hasMore: boolean;
  nextCursor: string | null;
}

/** `GET /api/v1/credit-notes` — one correction document row. */
export interface CreditNoteView {
  id: string;
  invoiceId: string | null;
  amount: string;
  taxAmount: string | null;
  currency: string;
  reason: string;
  status: string;
  issuedAt: string | null;
  appliedAt: string | null;
  createdAt: string;
}

/** `GET /api/v1/payment-methods` — tokens never leave the server (PCI SAQ-A). */
export interface PaymentMethodView {
  id: string;
  methodType: string;
  cardBrand: string | null;
  cardLast4: string | null;
  cardExpMonth: number | null;
  cardExpYear: number | null;
  walletType: string | null;
  isDefault: boolean;
  status: string;
  addedAt: string | null;
}

/** `GET /api/v1/billing/recovery-actions` — the required-action list. */
export interface RecoveryAction {
  type: "update_payment_method" | "payment_retry" | "grace_ending";
  severity: "required" | "warning";
  subscriptionId: string | null;
  serviceId: string | null;
  message: string;
  deadlineAt: string | null;
}

/** `POST /api/v1/coupons/validate` — the pre-checkout evaluation. */
export interface CouponEvaluation {
  valid: boolean;
  code: string;
  currency: string;
  subtotal: string;
  discount: {
    type: string;
    value: number;
    amount: string;
    freeTrialDays: number | null;
  };
  finalSubtotal: string;
}

/** `POST /api/v1/payment-methods/sessions` — the SCA card-add door. */
export interface CardSession {
  provider: string;
  iframeUrl: string;
  sessionToken: string;
}
